import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Star, Check, ShoppingCart, Package, Shield, Zap, ThumbsUp } from 'lucide-react';
import { toast } from 'sonner';

const ProductPage = () => {
  const navigate = useNavigate();
  const [quantity, setQuantity] = useState(1);

  const productImages = [
    'https://customer-assets.emergentagent.com/job_711851d8-a783-4abf-8185-8548b17cf2e3/artifacts/r1t3kgm3_image.png',
    'https://customer-assets.emergentagent.com/job_711851d8-a783-4abf-8185-8548b17cf2e3/artifacts/l3g8ky26_image.png',
    'https://customer-assets.emergentagent.com/job_711851d8-a783-4abf-8185-8548b17cf2e3/artifacts/bo43ocf5_image.png',
    'https://customer-assets.emergentagent.com/job_711851d8-a783-4abf-8185-8548b17cf2e3/artifacts/c1bnl5ex_image.png'
  ];

  const [selectedImage, setSelectedImage] = useState(productImages[0]);

  const pricingTiers = [
    { min: 1, max: 99, pricePerUnit: 12.99, discount: 0, label: 'Retail' },
    { min: 100, max: 199, pricePerUnit: 11.99, discount: 8, label: 'Contractor' },
    { min: 200, max: 499, pricePerUnit: 0, discount: 0, label: 'Partner', action: 'quote' },
    { min: 500, max: null, pricePerUnit: 0, discount: 0, label: 'Distributor', action: 'quote' }
  ];

  const currentTier = pricingTiers.find(tier => 
    quantity >= tier.min && (tier.max === null || quantity <= tier.max)
  ) || pricingTiers[0];

  const features = [
    '5 Color Temperature Settings (2700K-5000K)',
    'ETL, UL, CSA Certified',
    'IC-Rated & Wet Location Approved',
    'Dimmable (10-100%)',
    'Only 10W Power Consumption',
    '700 Lumens Output',
    '5-Year Warranty'
  ];

  const specifications = [
    { label: 'Power', value: '10W' },
    { label: 'Lumens', value: '700 LM' },
    { label: 'Color Temp', value: '2700K-5000K (5-CCT)' },
    { label: 'Dimmable', value: 'Yes (10-100%)' },
    { label: 'Certifications', value: 'ETL, UL, CSA' },
    { label: 'Rating', value: 'IC-Rated, Wet Location' },
    { label: 'Warranty', value: '5 Years' }
  ];

  const handleAddToCart = () => {
    if (currentTier.action === 'quote') {
      navigate('/bulk-pricing');
      toast.info('For 200+ units, please request a custom quote');
      return;
    }

    // Store cart data in localStorage
    const cartItem = {
      id: 'onyx-lighting-' + Date.now(),
      name: 'ONYX Ultra-Slim LED Potlight',
      price: currentTier.pricePerUnit,
      originalPrice: 12.99,
      quantity: quantity,
      discount: currentTier.discount,
      tier: currentTier.label,
      image: productImages[0]
    };

    const existingCart = JSON.parse(localStorage.getItem('cart') || '[]');
    existingCart.push(cartItem);
    localStorage.setItem('cart', JSON.stringify(existingCart));

    toast.success(`Added ${quantity} unit(s) to cart!`);
    navigate('/cart');
  };

  const calculateTotal = () => {
    if (!currentTier.pricePerUnit) return null;
    return (currentTier.pricePerUnit * quantity).toFixed(2);
  };

  const calculateSavings = () => {
    if (!currentTier.pricePerUnit || currentTier.discount === 0) return 0;
    return ((12.99 - currentTier.pricePerUnit) * quantity).toFixed(2);
  };

  // Comparison data
  const comparisonItems = [
    { 
      feature: 'Warranty', 
      onyx: '5 Years', 
      competitor: 'No Warranty',
      icon: <Shield className="w-5 h-5" />
    },
    { 
      feature: 'Power Efficiency', 
      onyx: '10W / 700LM', 
      competitor: '15-18W / 600LM',
      icon: <Zap className="w-5 h-5" />
    },
    { 
      feature: 'Certifications', 
      onyx: 'ETL, UL, CSA', 
      competitor: 'None or Limited',
      icon: <Shield className="w-5 h-5" />
    },
    { 
      feature: 'Color Temperature Options', 
      onyx: '5 Settings (2700K-5000K)', 
      competitor: 'Fixed Only',
      icon: <ThumbsUp className="w-5 h-5" />
    },
    { 
      feature: 'Dimmable', 
      onyx: 'Yes (10-100%)', 
      competitor: 'No',
      icon: <Check className="w-5 h-5" />
    },
    { 
      feature: 'Customer Rating', 
      onyx: '5.0 / 5.0', 
      competitor: '3.0 / 5.0',
      icon: <ThumbsUp className="w-5 h-5" />
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            <button onClick={() => { window.scrollTo(0, 0); navigate('/'); }} className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_elite-lighting/artifacts/27f7yllt_WhatsApp%20Image%202025-10-18%20at%2008.58.35.jpeg" 
                alt="ONYX Home Solutions" 
                className="h-12 lg:h-16 w-auto object-contain"
                style={{ background: 'transparent' }}
              />
            </button>
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => { window.scrollTo(0, 0); navigate('/'); }} className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors">
                Home
              </button>
              <button onClick={() => { window.scrollTo(0, 0); navigate('/products'); }} className="text-sm font-medium transition-colors" style={{ color: '#8B6F47' }}>
                Products
              </button>
              <button onClick={() => { window.scrollTo(0, 0); navigate('/bulk-pricing'); }} className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors">
                Bulk
              </button>
              <button onClick={() => { window.scrollTo(0, 0); navigate('/guide'); }} className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors">
                Guides
              </button>
              <button onClick={() => { window.scrollTo(0, 0); navigate('/become-partner'); }} className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors">
                Become a Partner
              </button>
              <button onClick={() => { window.scrollTo(0, 0); navigate('/admin/login'); }} className="text-sm font-medium transition-colors" style={{ color: '#8B6F47' }}>
                Login
              </button>
              <button onClick={() => { window.scrollTo(0, 0); navigate('/cart'); }} className="relative p-2 rounded-full hover:bg-gray-100 transition-colors" style={{ color: '#8B6F47' }}>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Product Content */}
      <div className="pt-32 pb-20 px-4 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Product Images */}
            <div>
              <div className="mb-4 rounded-3xl overflow-hidden bg-gray-50">
                <img 
                  src={selectedImage}
                  alt="ONYX Ultra-Slim LED Potlight"
                  className="w-full h-auto"
                />
              </div>
              <div className="grid grid-cols-4 gap-3">
                {productImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(img)}
                    className={`rounded-xl overflow-hidden border-2 transition-all ${
                      selectedImage === img ? 'ring-2' : 'border-gray-200 hover:border-gray-300'
                    }`}
                    style={{ borderColor: selectedImage === img ? '#D4A574' : undefined, ringColor: selectedImage === img ? '#8B6F47' : undefined }}
                  >
                    <img 
                      src={img}
                      alt={`View ${idx + 1}`}
                      className="w-full h-20 object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Product Info */}
            <div>
              <div className="inline-block mb-4 px-4 py-2 rounded-full" style={{ background: 'linear-gradient(135deg, #D4A574 0%, #8B6F47 100%)' }}>
                <span className="text-white text-sm font-bold">BEST SELLER</span>
              </div>
              
              <h1 className="text-4xl lg:text-5xl font-bold mb-4" style={{ color: '#1d1d1f' }}>
                ONYX Ultra-Slim LED Potlight
              </h1>
              
              <div className="flex items-center gap-2 mb-6">
                <div className="flex items-center">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-current text-yellow-400" />
                  ))}
                </div>
                <span className="text-gray-600">(200+ reviews)</span>
              </div>

              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Professional-grade 4" ONYX Potlight with 5 adjustable color temperatures. Perfect for residential and commercial applications. ETL, UL, and CSA certified.
              </p>

              {/* Pricing Section */}
              <div className="p-6 rounded-2xl mb-6" style={{ background: 'linear-gradient(135deg, #fef3e4 0%, #fde9cd 100%)' }}>
                <div className="flex items-baseline gap-3 mb-4">
                  {currentTier.pricePerUnit > 0 ? (
                    <>
                      <span className="text-4xl font-bold" style={{ color: '#8B6F47' }}>${currentTier.pricePerUnit.toFixed(2)}</span>
                      <span className="text-gray-600">per unit</span>
                      {currentTier.discount > 0 && (
                        <span className="text-lg line-through text-gray-500">$12.99</span>
                      )}
                    </>
                  ) : (
                    <span className="text-2xl font-bold" style={{ color: '#8B6F47' }}>
                      {currentTier.action === 'quote' ? 'Request Custom Quote' : 'Become a Partner'}
                    </span>
                  )}
                </div>
                <p className="text-sm font-semibold" style={{ color: '#8B6F47' }}>
                  Current Tier: {currentTier.label} {currentTier.discount > 0 && `(${currentTier.discount}% OFF)`}
                </p>
              </div>

              {/* Quantity Selector */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Quantity
                </label>
                <input 
                  type="number"
                  min="1"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none text-lg font-semibold"
                  style={{ borderColor: '#D4A574' }}
                />
                <div className="flex flex-wrap gap-2 mt-3">
                  {[1, 10, 50, 100].map(qty => (
                    <button
                      key={qty}
                      onClick={() => setQuantity(qty)}
                      className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                      style={{ background: '#fef3e4', color: '#8B6F47' }}
                    >
                      {qty}
                    </button>
                  ))}
                </div>
              </div>

              {/* Total */}
              {currentTier.pricePerUnit > 0 && (
                <div className="bg-gray-50 rounded-2xl p-6 mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-600">Subtotal ({quantity} units)</span>
                    <span className="text-2xl font-bold">${calculateTotal()}</span>
                  </div>
                  {currentTier.discount > 0 && (
                    <div className="flex items-center justify-between text-green-600">
                      <span className="text-sm">You save</span>
                      <span className="text-xl font-bold">${calculateSavings()}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Add to Cart Button */}
              <Button 
                onClick={handleAddToCart}
                className="w-full text-white py-5 rounded-full text-lg font-medium mb-4"
                style={{ background: 'linear-gradient(135deg, #D4A574 0%, #8B6F47 100%)' }}
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                {currentTier.action === 'quote' ? 'Request Quote' : currentTier.action === 'partner' ? 'Become a Partner' : 'Add to Cart'}
              </Button>

              <button
                onClick={() => { window.scrollTo(0, 0); navigate('/bulk-pricing'); }}
                className="w-full text-center py-3 rounded-full text-base font-medium transition-colors hover:bg-gray-50"
                style={{ color: '#8B6F47' }}
              >
                View Bulk Pricing
              </button>

              {/* Features */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <h3 className="text-xl font-bold mb-4" style={{ color: '#1d1d1f' }}>Key Features</h3>
                <div className="space-y-3">
                  {features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <Check className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#D4A574' }} />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Product Comparison Section */}
          <div className="mt-20 bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl p-8 lg:p-12">
            <div className="text-center mb-10">
              <h2 className="text-3xl lg:text-4xl font-bold mb-4" style={{ color: '#1d1d1f' }}>
                Why Choose ONYX?
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                See how ONYX Ultra-Slim LED Potlights compare to typical competitors in the market.
              </p>
            </div>

            <div className="max-w-5xl mx-auto">
              <div className="grid gap-4">
                {/* Header Row */}
                <div className="grid grid-cols-3 gap-4 pb-4 border-b-2 border-amber-200">
                  <div className="text-sm font-semibold text-gray-500 uppercase">Feature</div>
                  <div className="text-center">
                    <div className="inline-block px-4 py-2 rounded-full" style={{ background: 'linear-gradient(135deg, #D4A574 0%, #8B6F47 100%)' }}>
                      <span className="text-white font-bold text-sm">ONYX</span>
                    </div>
                  </div>
                  <div className="text-center text-sm font-semibold text-gray-500 uppercase">Typical Competitor</div>
                </div>

                {/* Comparison Rows */}
                {comparisonItems.map((item, idx) => (
                  <div key={idx} className="grid grid-cols-3 gap-4 items-center p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-3">
                      <div className="text-amber-600">{item.icon}</div>
                      <span className="font-semibold text-gray-800">{item.feature}</span>
                    </div>
                    <div className="text-center">
                      <span className="font-bold text-green-700 text-lg">{item.onyx}</span>
                    </div>
                    <div className="text-center">
                      <span className="text-gray-500 text-sm">{item.competitor}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 p-6 bg-white rounded-xl border-2" style={{ borderColor: '#D4A574' }}>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #D4A574 0%, #8B6F47 100%)' }}>
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-2" style={{ color: '#1d1d1f' }}>Industry-Leading 5-Year Warranty</h4>
                    <p className="text-gray-600">
                      Unlike typical competitors who offer NO WARRANTY at all, ONYX stands behind every product with a comprehensive 5-year warranty. We're so confident in our superior quality that we guarantee it for years, not months.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Specifications */}
          <div className="mt-20">
            <h2 className="text-3xl font-bold mb-8" style={{ color: '#1d1d1f' }}>Technical Specifications</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {specifications.map((spec, idx) => (
                <div key={idx} className="p-6 rounded-2xl bg-gray-50">
                  <p className="text-sm font-medium text-gray-500 mb-1">{spec.label}</p>
                  <p className="text-lg font-bold" style={{ color: '#1d1d1f' }}>{spec.value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-12 px-4 lg:px-8 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <p className="text-gray-400 text-sm">Premium LED lighting and HVAC solutions for modern homes.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Products</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => { window.scrollTo(0, 0); navigate('/products'); }} className="text-gray-400 hover:text-white">ONYX Lighting</button></li>
                <li><button onClick={() => { window.scrollTo(0, 0); navigate('/products'); }} className="text-gray-400 hover:text-white">ONYX HVAC</button></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => { window.scrollTo(0, 0); navigate('/'); }} className="text-gray-400 hover:text-white">About Us</button></li>
                <li><button onClick={() => { window.scrollTo(0, 0); navigate('/become-partner'); }} className="text-gray-400 hover:text-white">Become a Partner</button></li>
                <li><button onClick={() => { window.scrollTo(0, 0); navigate('/guide'); }} className="text-gray-400 hover:text-white">Guides</button></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="mailto:info@onyx-homesolutions.com" className="text-gray-400 hover:text-white">info@onyx-homesolutions.com</a></li>
                <li><a href="tel:519-996-2866" className="text-gray-400 hover:text-white">519-996-2866</a></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-gray-800 text-center text-sm text-gray-400">
            <p>© 2025 ONYX Home Solutions. All rights reserved. | Powered by Syntrexe</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ProductPage;